import { FC, ReactElement, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { useUpdateSCAccountProcessStatus } from '@/modules/Domains';
import {
    SitesQueryKeys,
    useChangeVisibilityOfContentUpdateDateMutation,
    useDeleteSitesMutation,
    useGetDossierByIdSitesQuery,
} from '@/modules/Sites';

import { useQueryClient } from 'react-query';
import { SitesDossierView } from './SitesDossierView';

interface Props {
  siteId: string;
}

export const SitesDossierContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: siteDossierData,
    error: siteDossierError,
    isLoading: isSiteDossierLoading,
    isFetching: isSiteDossierFetching,
    isError: isSiteDossierError,
  } = useGetDossierByIdSitesQuery(siteId)({
    enabled: !!siteId,
  });

  const { mutate: deleteSiteMutation, isLoading: isDeleteSiteLoading } = useDeleteSitesMutation(siteId)({
    onSuccess: () => {
      appMessageApi.success('Сайт видалено успішно!');
      navigate(AppRoutes.Sites);
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    },
  });

  const { mutate: updateContentLastDateVisibilityMutation, isLoading: isContentLastDateVisibilityLoading } =
    useChangeVisibilityOfContentUpdateDateMutation(siteId)({
      onSuccess: (data) => {
        appMessageApi.success('Налаштування змінено!');
        queryClient.setQueryData([SitesQueryKeys.Dossier, siteId], () => {
          return data;
        });
        queryClient.setQueryData([SitesQueryKeys.GetContentById, siteId], () => {
          return data;
        });
      },
      onError: (err) => {
        messageOnError(err, appMessageApi);
      },
    });

  useUpdateSCAccountProcessStatus({
    pageParam: null,
    domainId: null,
    siteId,
  });

  const handleDeleteSite = useCallback((): void => {
    deleteSiteMutation();
  }, []);

  const handleChangeVisibilityOfContentUpdateDate = useCallback((value: boolean): void => {
    updateContentLastDateVisibilityMutation({ visibilityOfContentUpdateDate: value });
  }, []);

  return (
    <>
      <FallbackedLayout
        data={siteDossierData}
        isLoading={isSiteDossierLoading}
        error={siteDossierError}
        isError={isSiteDossierError}
      >
        {(site): ReactElement => (
          <>
            <SitesDossierView
              site={site}
              isDeleteSiteLoading={isDeleteSiteLoading}
              isButtonsDisabled={isSiteDossierLoading || isSiteDossierFetching || isContentLastDateVisibilityLoading}
              isContentLastDateVisibilityLoading={isContentLastDateVisibilityLoading}
              handleDeleteSite={handleDeleteSite}
              handleChangeVisibilityOfContentUpdateDate={handleChangeVisibilityOfContentUpdateDate}
            />
          </>
        )}
      </FallbackedLayout>
    </>
  );
});
